import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import dayjs from 'dayjs'
import { Capacitor } from '@capacitor/core'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { BACKGROUND_OPERATION } from '@/utils/backgroundOperation'
import { appInForeground } from '@/utils/appState'
import {
  GET_CATALOG_CHANGES,
  GET_FILTERED_NOVELTIES,
  GET_FILTERED_COLLECTION_ENTRIES,
  GET_NOVELTIES,
  GET_RELEASE_ENTRIES,
} from '@/queries'
import {
  GET_FILTERED_CUSTOM_LIST_NOVELTIES,
  GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES,
  GET_DATABASE_LIST_CURATED_ENTRIES,
  GET_USER_LIST_CURATED_ENTRIES,
  GET_USER_LIST_CURATED_NOVELTIES,
  GET_NOVELTY_ID_BY_RELATION,
  USER_CUSTOM_LIST_STATS,
} from '@/queries/customLists.ts'
import type { VariablesOf } from '@graphql-typed-document-node/core'
import { OrderDirection, UserCustomListTargetTypeType } from '../../../__generated__/graphql.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useInfoStore } from '@/stores/info.ts'
import { useLocalizationStore } from '@/stores/localization.ts'
import { showToast } from '@/utils/showToast.ts'
import { usePreferencesStore, ALL_CATALOG_SUBSCRIPTION_TYPES } from '@/stores/preferences.ts'
import { useUserCollectionStore, type UserCollection } from '@/stores/user-collection'
import { useUserListsStore } from '@/stores/user-lists'
import { useUserSyncStore, type CollectionDeltaRow } from '@/stores/user-sync'
import {
  mirrorUserCollection,
  refreshListNoveltyMembership,
  refreshListReleaseEntryMembership,
  type MirrorEntry,
} from '@/data/userDataTables.ts'
import { getCatalogExecutor, flushCatalogStore, closeCatalogStore } from '@/stores/sync/sqlite.ts'
import type { SqliteExecutor } from '@/data/sqliteExecutor.ts'
import {
  applyDelta,
  getCursor,
  setCursor,
  getEntityVersion,
  setEntityVersion,
  getSubscribedTypes,
  setSubscribedTypes,
  getLastSyncedAt,
  setLastSyncedAt,
  getCoverPackTier,
  setCoverPackTier,
  getReplicaStorage,
  wipeEntity,
  prune,
  REPLICA_VERSION,
} from '@/stores/sync/catalogReplica.ts'
import {
  clearCoverCache,
  collectCoverKeys,
  collectSupersededCoverStrings,
  downloadCoverArchives,
  fetchMissingCovers,
  getCoverStoreBytes,
  pruneCoverCache,
  pruneSupersededCovers,
  type PackTier,
} from '@/stores/sync/coverPack.ts'
import {
  bootstrapReplicaFromBlobs,
  applyTypeBlobs,
  type BootstrapOutcome,
  type SyncPhase,
  type ProgressReport,
} from '@/stores/sync/catalogBootstrap.ts'
import {
  queryEntity,
  queryFilteredNovelties,
  queryFilteredCustomListNovelties,
  queryFilteredCollectionEntries,
  queryFilteredCustomListReleaseEntries,
  queryDatabaseListReleaseEntries,
  queryUserCustomListStats,
} from '@/data/catalogReplicaRepo.ts'
import { ENTITY_REPLICA_CONFIGS } from '@/data/entityReplicaConfig.generated.ts'
import { CATALOG_OPS } from '@/data/catalogOps.generated.ts'
import { CATALOG_NESTED_OPS } from '@/data/catalogNestedOps.generated.ts'
import { resolveNestedOp } from '@/data/catalogNestedRepo.ts'
import { catalogQuery, registerCatalogOpLoose, setReplicaGate } from '@/data/catalogClient.ts'

const PAGE_LIMIT = 500
type CatalogSubscription = VariablesOf<typeof GET_CATALOG_CHANGES>['subscription']

const CONFIG_BY_ENTITY = new Map<string, (typeof ENTITY_REPLICA_CONFIGS)[number]>(
  ENTITY_REPLICA_CONFIGS.map((config) => [config.entity, config]),
)
function configFor(entity: string): (typeof ENTITY_REPLICA_CONFIGS)[number] {
  const config = CONFIG_BY_ENTITY.get(entity)
  if (!config) throw new Error(`No replica config for entity: ${entity}`)
  return config
}

type OrderByArg = Record<string, 'asc' | 'desc'>

type CatalogReadVariables = {
  where?: Record<string, unknown>
  // Single-or-array mirrors GraphQL list-input coercion; the shared query-core
  // builder normalizes it, so both shapes read identically offline and online.
  orderBy?: OrderByArg | OrderByArg[]
  take?: number | null
  skip?: number
}

// Flatten one optimistic collection entry to the server's UserReleaseEntryStatus
// row shape for the SQLite mirror. ownedMap/miscOwnedMap/latestReadMap already
// match the raw itemStatus/miscItemStatus/readStatus JSON; the row id falls back
// to the entryId for not-yet-synced entries (it only links the read-status row).
function toMirrorEntry(
  entryId: string,
  collection: UserCollection,
  raw: CollectionDeltaRow | undefined,
): MirrorEntry {
  return {
    id: collection.id ?? entryId,
    entryId,
    itemStatus: collection.ownedMap,
    miscItemStatus: collection.miscOwnedMap,
    collectionStatus: collection.collectionStatus,
    currentReadStatus: collection.currentReadStatus,
    startDate: collection.collectionStartDate,
    endDate: collection.collectionEndDate,
    readStatus: collection.latestReadMap,
    internalName: raw?.internalName ?? null,
    publisherName: raw?.publisherName ?? null,
    type: raw?.type ?? null,
    tagSearchField: raw?.tagSearchField ?? null,
    demographySearchField: raw?.demographySearchField ?? null,
    titleSearch: raw?.titleSearch ?? null,
    totalVolumes: raw?.totalVolumes ?? null,
    totalNotReleasedVolumes: raw?.totalNotReleasedVolumes ?? null,
    totalReleasedVolumes: raw?.totalReleasedVolumes ?? null,
    lastCollectionChanged: raw?.lastCollectionChanged ?? null,
    lastReadChanged: raw?.lastReadChanged ?? null,
    createdAt: raw?.createdAt ?? null,
    compoundKey: raw?.compoundKey ?? null,
    popularityScore: raw?.popularityScore ?? null,
    recommendationScore: raw?.recommendationScore ?? null,
  }
}

export const useCatalogSyncStore = defineStore('catalog-sync', () => {
  const { hasPremium, isTester, authState } = storeToRefs(useAuthStore())
  const { connected, isWifi } = storeToRefs(useInfoStore())
  const { catalogSubscription, offlineImageTier, offlineModeEnabled, forceOffline } =
    storeToRefs(usePreferencesStore())
  const { setOfflineModeEnabled, setForceOffline } = usePreferencesStore()
  const { t } = useLocalizationStore()

  const packTier = computed<PackTier | null>(() =>
    offlineImageTier.value === '96' ||
    offlineImageTier.value === '192' ||
    offlineImageTier.value === '384'
      ? offlineImageTier.value
      : null,
  )

  // Every install replicates the full catalog — per-type subscription UX was
  // dropped (packs too small to matter yet); the protocol keeps the dimension for
  // later. Omitting `types` means "all" (the server filter is NULL-inclusive).
  const forwardSubscription = computed<CatalogSubscription>(() => ({
    regions: catalogSubscription.value.regions,
  }))

  // Surfaced in Settings "Offline-Daten". `syncing` reflects any in-flight pull
  // (forward sync, shape backfill, or a widen reconcile); `lastSyncedAt` is the
  // ISO time of the last completed forward sync, persisted in the replica.
  const syncing = ref(false)
  const lastSyncedAt = ref<string | null>(null)
  // True when a fresh install could not seed from a snapshot blob (unconfigured /
  // unreachable / version-incompatible). The offline catalog is unavailable until
  // a retry succeeds — we deliberately do not drain the whole feed as a fallback.
  const replicaUnavailable = ref(false)
  // Determinate progress for the active drain: how far the cursor has advanced
  // through the seq range toward the feed head. Null when no total is known yet.
  const syncProgress = ref<{ done: number; total: number } | null>(null)
  // Coarse phase label for the UI: bootstrap goes downloading -> extracting ->
  // applying per blob, forward sync is 'updating'. Null when idle.
  const syncPhase = ref<SyncPhase | null>(null)
  // All replica-mutating work runs through this single serial queue. Forward sync,
  // the shape/widen backfills, and the subscription reconcile each wipe + rewrite
  // tables; two of them interleaving (e.g. init() racing the reconnect watcher)
  // would let one wipeEntity-all while another refills — the storage size then
  // ping-pongs and the cursor thrashes. Serializing makes each run see a settled
  // replica. Errors are swallowed only for the chain link, not the caller's promise.
  let syncTail: Promise<unknown> = Promise.resolve()
  function serialize<T>(run: () => Promise<T>): Promise<T> {
    const result = syncTail.then(run, run)
    syncTail = result.then(
      () => {},
      () => {},
    )
    return result
  }

  let activeSyncs = 0
  async function track<T>(run: () => Promise<T>): Promise<T> {
    activeSyncs += 1
    syncing.value = true
    try {
      return await run()
    } finally {
      activeSyncs -= 1
      if (activeSyncs === 0) {
        await flushCatalogStore()
        syncing.value = false
        syncProgress.value = null
        syncPhase.value = null
      }
    }
  }

  const reportProgress: ProgressReport = (report) => {
    syncPhase.value = report.phase
    syncProgress.value = report.total > 0 ? { done: report.done, total: report.total } : null
  }

  // Seq is a monotonic cursor, so (nextSeq - start) / (headSeq - start) is real
  // linear progress even though compaction leaves gaps in the seq values.
  function updateProgress(startSeq: string, page: { nextSeq: string; headSeq: string }) {
    const start = Number(startSeq)
    const total = Math.max(Number(page.headSeq) - start, 0)
    const done = Math.min(Math.max(Number(page.nextSeq) - start, 0), total)
    syncProgress.value = total > 0 ? { done, total } : null
  }

  async function loadSyncMeta(): Promise<void> {
    if (!offlineEnabled.value) return
    const executor = await getCatalogExecutor()
    lastSyncedAt.value = (await getLastSyncedAt(executor)) ?? null
  }

  async function getStorage(): Promise<{ rows: number; bytes: number }> {
    const executor = await getCatalogExecutor()
    return getReplicaStorage(executor)
  }

  async function getImageStorage(): Promise<number> {
    return getCoverStoreBytes()
  }

  // In dev the browser SQLite replica (jeep-sqlite/wasm) is enabled so offline
  // mode can be tested without a device; prod stays native + premium (testers
  // included, so they can exercise offline catalog + force-offline on device).
  const canUseOffline = computed(
    () =>
      import.meta.env.DEV || (Capacitor.isNativePlatform() && (hasPremium.value || isTester.value)),
  )
  // Offline mode is now opt-in: the entitlement (canUseOffline) plus the user's
  // explicit toggle (offlineModeEnabled). Until enabled, no replica is built and
  // reads stay on the network — nothing downloads on boot/login.
  const offlineEnabled = computed(() => canUseOffline.value && offlineModeEnabled.value === true)

  // The two big one-time downloads — the initial catalog snapshot blob (~170 MB)
  // and the cover-pack archives (~120 MB) — are WiFi-gated so a metered cellular
  // plan is never spent on them unattended. Incremental forward-sync updates are
  // small and stay ungated (run on any network). Non-native/dev has no metering
  // concept, so it always downloads. `forceCellular` is the one-shot override the
  // Settings "download now" button sets. `awaitingWifi` drives the waiting UI.
  const forceCellular = ref(false)
  const awaitingWifi = ref(false)
  // Aborts in-flight replica writes (bootstrap chunks, forward drain) at logout so
  // the DB can be closed with no open transaction before the webview reloads —
  // otherwise an interrupted chunk transaction strands the SQLite write lock and
  // the next session can't reopen the replica ("database is locked").
  let syncAbort = new AbortController()
  const tearingDown = ref(false)
  // Undo a previous logout teardown so a re-login that reuses this JS context (no
  // webview reload) isn't left permanently unable to sync.
  function renewSyncAbort(): void {
    if (syncAbort.signal.aborted) syncAbort = new AbortController()
  }
  function resetTeardown(): void {
    renewSyncAbort()
    tearingDown.value = false
  }
  function abortSyncAtNextCursorCheckpoint(): void {
    syncAbort.abort()
    coverPackAbort?.abort()
  }
  // Gated by tearingDown so nothing re-enters a replica write once logout teardown
  // begins (the mirror watchers check it too).
  const canSync = computed(() => offlineEnabled.value && connected.value && !tearingDown.value)
  const isNativePlatform = Capacitor.isNativePlatform()
  const canDownloadBlobs = computed(() => !isNativePlatform || isWifi.value || forceCellular.value)

  // Generated from the app query documents (catalogOps.generated.ts): every
  // catalog read whose selection set is fully serviceable from the replica blob.
  // One generic handler — no per-entity code. Unregistered/online-only ops fall
  // through to the network via catalogClient.
  for (const op of CATALOG_OPS) {
    registerCatalogOpLoose(op.document, async (variables) => {
      const executor = await getCatalogExecutor()
      const args = variables as CatalogReadVariables
      const rows = await queryEntity(executor, configFor(op.entity), op.outputFields, {
        where: args.where,
        orderBy: args.orderBy,
        take: args.take,
        skip: args.skip,
      })
      return { data: { [op.field]: rows } }
    })
  }

  // Override for GET_NOVELTY_ID_BY_RELATION: its filter/limit are hardcoded in the
  // query with a scalar `$relationId` variable, so the generic handler (which only
  // reads the $where/$take convention) would see no filter and return an arbitrary
  // first novelty. Read the scalar variable and apply the filter explicitly.
  registerCatalogOpLoose(GET_NOVELTY_ID_BY_RELATION, async (variables) => {
    const executor = await getCatalogExecutor()
    const { relationId } = variables as { relationId: string }
    const rows = await queryEntity(executor, configFor('Novelty'), ['id'], {
      where: { relationId: { equals: relationId } },
      take: 1,
    })
    return { data: { novelties: rows } }
  })

  // Generated nested reads (cross-relation selections): one generic resolver walks
  // each op's plan tree against the replica — no per-query code.
  for (const op of CATALOG_NESTED_OPS) {
    registerCatalogOpLoose(op.document, async (variables) => {
      const executor = await getCatalogExecutor()
      return resolveNestedOp(executor, op, variables as CatalogReadVariables)
    })
  }

  // filteredNovelties is codegen-skipped (its collectionVolumeInfo isn't a plain
  // blob field), so it's wired by hand: the shared atsume-query-core builder runs
  // the collection-aware join against the replica + the user-collection mirror,
  // then collectionVolumeInfo is reconstructed from the ReleaseItem(Collection)
  // replicas.
  registerCatalogOpLoose(GET_FILTERED_NOVELTIES, async (variables) => {
    const executor = await getCatalogExecutor()
    const args = variables as CatalogReadVariables & { filterMode?: string | null }
    const rows = await queryFilteredNovelties(executor, {
      where: args.where,
      orderBy: args.orderBy,
      take: args.take,
      skip: args.skip,
      filterMode: args.filterMode,
    })
    return { data: { filteredNovelties: rows } }
  })

  // A list's current member ids straight from the optimistic user-lists store —
  // the source of truth for membership, including still-unsynced edits.
  const memberEntityKeys = (listId: string): string[] =>
    (listsState.value[listId]?.members ?? []).map((member) => member.entityKey)

  // Materialize a list's membership into SQLite so the filtered join can use it.
  // Two things matter here: every write must go through serialize() (the DB has a
  // single transaction slot — concurrent list previews would otherwise collide with
  // "Already in transaction"), and it must be skipped when nothing changed, because
  // each transaction flushes the whole DB to IndexedDB. Steady-state reads do no
  // writes at all; only an actual membership edit pays for one.
  const writtenMembership = new Map<string, string>()

  async function ensureListMembership(
    listId: string,
    entityKeys: string[],
    write: (executor: SqliteExecutor, listId: string, ids: readonly string[]) => Promise<void>,
  ): Promise<void> {
    const signature = entityKeys.join(',')
    if (writtenMembership.get(listId) === signature) return
    await serialize(async () => {
      const executor = await getCatalogExecutor()
      await write(executor, listId, entityKeys)
    })
    writtenMembership.set(listId, signature)
  }

  // filteredCustomListNovelties: same hand-wired path, scoped to a list's curated
  // membership. The membership rows are refreshed just-in-time from the optimistic
  // store right before the join, so the filter always sees the latest edit and the
  // join still scales (no giant IN-list, no background mirror to go stale).
  registerCatalogOpLoose(GET_FILTERED_CUSTOM_LIST_NOVELTIES, async (variables) => {
    const executor = await getCatalogExecutor()
    const args = variables as CatalogReadVariables & {
      listId: string
      filterMode?: string | null
    }
    await ensureListMembership(
      args.listId,
      memberEntityKeys(args.listId),
      refreshListNoveltyMembership,
    )
    const rows = await queryFilteredCustomListNovelties(executor, {
      listId: args.listId,
      where: args.where,
      orderBy: args.orderBy,
      take: args.take,
      skip: args.skip,
      filterMode: args.filterMode,
    })
    return { data: { filteredCustomListNovelties: rows } }
  })

  // filteredReleaseEntries (curated release-entry lists): membership ⋈ ReleaseEntry
  // ⋈ ownership, allDone/missing/gaps/unowned/unread via the shared builder.
  registerCatalogOpLoose(GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES, async (variables) => {
    const executor = await getCatalogExecutor()
    const args = variables as CatalogReadVariables & { listId: string; filterMode?: string | null }
    await ensureListMembership(
      args.listId,
      memberEntityKeys(args.listId),
      refreshListReleaseEntryMembership,
    )
    const rows = await queryFilteredCustomListReleaseEntries(executor, {
      listId: args.listId,
      where: args.where,
      orderBy: args.orderBy,
      take: args.take,
      skip: args.skip,
      filterMode: args.filterMode,
    })
    return { data: { filteredReleaseEntries: rows } }
  })

  // userCustomListStats: counts + total value for a list. Membership is the
  // optimistic local set (materialized just-in-time); the counting/summing is the
  // shared query-core node, so it matches the server exactly. Needs the local
  // catalogue for prices, so it only serves locally when entitled (else server).
  registerCatalogOpLoose(USER_CUSTOM_LIST_STATS, async (variables) => {
    const executor = await getCatalogExecutor()
    const { listId } = variables as { listId: string }
    const list = listsState.value[listId]
    const targetType =
      list?.targetType === UserCustomListTargetTypeType.Novelty ? 'novelty' : 'releaseEntry'
    const write =
      targetType === 'novelty' ? refreshListNoveltyMembership : refreshListReleaseEntryMembership
    await ensureListMembership(listId, memberEntityKeys(listId), write)
    const userCustomListStats = await queryUserCustomListStats(executor, { listId, targetType })
    return { data: { userCustomListStats } }
  })

  // databaseListReleaseEntries (curated database lists): membership ⋈ ReleaseEntry,
  // filtered by the databaseList FK (a relation the scalar codegen can't serve, so
  // hand-wired) + the ReleaseEntry sub-where, ordered by orderNumber.
  registerCatalogOpLoose(GET_DATABASE_LIST_CURATED_ENTRIES, async (variables) => {
    const executor = await getCatalogExecutor()
    const v = variables as {
      where?: {
        databaseList?: { id?: { equals?: string } }
        releaseEntry?: Record<string, unknown>
      }
      orderBy?: { orderNumber?: 'asc' | 'desc' }[]
      take?: number | null
      skip?: number | null
    }
    const databaseListId = v.where?.databaseList?.id?.equals
    if (!databaseListId) return { data: { databaseListReleaseEntries: [] } }
    const rows = await queryDatabaseListReleaseEntries(executor, {
      databaseListId,
      where: v.where?.releaseEntry ?? {},
      direction: v.orderBy?.[0]?.orderNumber === 'desc' ? 'desc' : 'asc',
      take: v.take,
      skip: v.skip ?? 0,
    })
    return { data: { databaseListReleaseEntries: rows } }
  })

  // filteredUserReleaseEntryStatuses (collection lists): allDone/missing/gaps via
  // the shared builder, filtering the user mirror and joining entry{} from the
  // catalog ReleaseEntry replica.
  registerCatalogOpLoose(GET_FILTERED_COLLECTION_ENTRIES, async (variables) => {
    const executor = await getCatalogExecutor()
    const args = variables as CatalogReadVariables & { filterMode?: string | null }
    const rows = await queryFilteredCollectionEntries(executor, {
      where: args.where,
      orderBy: args.orderBy,
      take: args.take,
      skip: args.skip,
      filterMode: args.filterMode,
    })
    return { data: { filteredUserReleaseEntryStatuses: rows } }
  })

  type CuratedVariables = {
    where?: { list?: { id?: { equals?: string } } }
    orderBy?: { orderNumber?: 'asc' | 'desc' }[]
    take?: number | null
    skip?: number | null
  }

  function curatedPage<T extends { orderNumber?: number | null }>(
    members: readonly T[],
    variables: CuratedVariables,
  ): T[] {
    const direction = variables.orderBy?.[0]?.orderNumber === 'desc' ? -1 : 1
    const sorted = [...members].sort(
      (a, b) => ((a.orderNumber ?? 0) - (b.orderNumber ?? 0)) * direction,
    )
    const skip = variables.skip ?? 0
    const end = variables.take == null ? undefined : skip + variables.take
    return sorted.slice(skip, end)
  }

  // Curated-manual list contents: membership (which items are in the list) is user
  // data and is read from the optimistic user-lists store ALWAYS — never the server
  // — so it reflects an edit the instant it is made, identically online and offline.
  // Only the catalog DETAILS for those ids go through the transparent catalogQuery
  // (server online / replica offline). listsState carries the member order.
  type CuratedMember = { id: string; orderNumber: number; entityId: string }

  function curatedMembers(listId: string | undefined): CuratedMember[] {
    const members = listId ? (listsState.value[listId]?.members ?? []) : []
    return members.map((member, index) => ({
      id: member.itemId ?? member.entityKey,
      orderNumber: index,
      entityId: member.entityKey,
    }))
  }

  registerCatalogOpLoose(GET_USER_LIST_CURATED_ENTRIES, async (variables) => {
    const v = variables as CuratedVariables
    const members = curatedPage(curatedMembers(v.where?.list?.id?.equals), v)
    const ids = members.map((m) => m.entityId)
    const { data } = await catalogQuery({
      query: GET_RELEASE_ENTRIES,
      variables: { where: { id: { in: ids } }, orderBy: [{ id: OrderDirection.Asc }], skip: 0 },
    })
    const byId = new Map((data?.releaseEntries ?? []).map((entry) => [entry.id, entry]))
    const items = members.flatMap((m) => {
      const releaseEntry = byId.get(m.entityId)
      return releaseEntry
        ? [
            {
              __typename: 'UserCustomListReleaseEntryItem',
              id: m.id,
              orderNumber: m.orderNumber,
              releaseEntry,
            },
          ]
        : []
    })
    return { data: { userCustomListReleaseEntryItems: items } }
  })

  registerCatalogOpLoose(GET_USER_LIST_CURATED_NOVELTIES, async (variables) => {
    const v = variables as CuratedVariables
    const members = curatedPage(curatedMembers(v.where?.list?.id?.equals), v)
    const ids = members.map((m) => m.entityId)
    const { data } = await catalogQuery({
      query: GET_NOVELTIES,
      variables: { where: { id: { in: ids } }, orderBy: [{ id: OrderDirection.Asc }], skip: 0 },
    })
    const byId = new Map((data?.novelties ?? []).map((novelty) => [novelty.id, novelty]))
    const items = members.flatMap((m) => {
      const novelty = byId.get(m.entityId)
      return novelty
        ? [
            {
              __typename: 'UserCustomListNoveltyItem',
              id: m.id,
              orderNumber: m.orderNumber,
              novelty,
            },
          ]
        : []
    })
    return { data: { userCustomListNoveltyItems: items } }
  })

  // Membership (which items are in a list) is user data — always read it locally,
  // for everyone, online or offline. Its catalog details still resolve transparently
  // (the catalogQuery inside the handler). Filtered curated lists must filter over
  // the catalogue, so they read locally only when the offline catalogue exists
  // (entitled); otherwise they fall through to the server. Everything else keeps the
  // plain online→server / offline→replica behavior.
  const membershipAlwaysLocal = new Set([
    GET_USER_LIST_CURATED_NOVELTIES,
    GET_USER_LIST_CURATED_ENTRIES,
  ])
  const curatedLocalWhenEntitled = new Set([
    GET_FILTERED_CUSTOM_LIST_NOVELTIES,
    GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES,
    USER_CUSTOM_LIST_STATS,
  ])
  // Read the catalogue locally whenever we're not connected (offline device,
  // forced offline, or server down). Entitlement (offlineEnabled) still gates it,
  // since only entitled users have a replica.
  setReplicaGate((document) => {
    if (membershipAlwaysLocal.has(document)) return true
    if (curatedLocalWhenEntitled.has(document)) return offlineEnabled.value
    return offlineEnabled.value && !connected.value
  })

  // Keep the SQLite mirror of the user's collection current so the offline
  // filteredNovelties join has fresh ownership/read/collecting-window data the
  // moment the device drops offline. Debounced to coalesce rapid optimistic edits.
  const { collectionMap } = storeToRefs(useUserCollectionStore())
  // The delta-synced raw UserReleaseEntryStatus rows carry the denormalized
  // scalars (type/publisher/search/totals) the collection filter needs but the
  // optimistic collectionMap doesn't. Keyed by entry id for the mirror join.
  const userSync = useUserSyncStore()
  // The optimistic list/membership map — the source of truth for what's in a list,
  // including still-unsynced adds/removes. Curated replica reads overlay it onto
  // the synced base so the offline replica reflects edits before they sync.
  const { listsState } = storeToRefs(useUserListsStore())
  const rawByEntry = computed(() => {
    const map = new Map<string, (typeof userSync.collectionRows)[number]>()
    for (const row of userSync.collectionRows) {
      if (row.entry?.id) map.set(row.entry.id, row)
    }
    return map
  })
  // serialize(): the mirror opens its own transaction, and the native SQLite
  // connection has exactly one transaction slot — racing a bootstrap/sync
  // transaction throws "Already in transaction" and kills the whole sync run.
  const mirrorCollection = useDebounceFn(async () => {
    if (!offlineEnabled.value || tearingDown.value) return
    await serialize(async () => {
      const executor = await getCatalogExecutor()
      const entries = Object.entries(collectionMap.value).map(([entryId, collection]) =>
        toMirrorEntry(entryId, collection, rawByEntry.value.get(entryId)),
      )
      await mirrorUserCollection(executor, entries)
    })
  }, 800)
  watch([collectionMap, () => userSync.revision, offlineEnabled], () => void mirrorCollection(), {
    deep: true,
    immediate: true,
  })

  // No background membership mirror: curated-list membership is refreshed
  // just-in-time from the optimistic user-lists store inside the filtered handlers
  // (see refreshList*Membership above), so it can never be stale.

  // Instant reaction to a tier change while online; the stored-tier diff in
  // reconcileCoverPack (run by every forward sync) catches a change made offline
  // or an interrupted download on reconnect. Aborting first makes a mid-download
  // toggle cut the running pack download short instead of finishing a stale tier —
  // the queued reconcile then starts over against the new preference.
  watch(offlineImageTier, () => {
    coverPackAbort?.abort()
    void serialize(() => track(reconcileCoverPack))
  })

  async function pullPage(sinceSeq: string, subscription: CatalogSubscription) {
    const { data } = await apolloClient.query({
      query: GET_CATALOG_CHANGES,
      variables: { sinceSeq, subscription, limit: PAGE_LIMIT },
      fetchPolicy: 'network-only',
      context: BACKGROUND_OPERATION,
    })
    return data.catalogChanges ?? null
  }

  // Seed a fresh replica from prebuilt snapshot blobs (one shared slice + one per
  // subscribed type). Blobs are **mandatory** on a fresh install: we never drain
  // catalogChanges from seq 0 (hundreds of pages + per-collection relation fan-out
  // on the server). If the blob is unavailable the bootstrap is left undone and the
  // caller aborts rather than falling back to the full drain.
  async function tryBootstrap(executor: SqliteExecutor): Promise<BootstrapOutcome> {
    return bootstrapReplicaFromBlobs({
      executor,
      types: [...ALL_CATALOG_SUBSCRIPTION_TYPES],
      region: catalogSubscription.value.regions[0] ?? 'dach',
      report: reportProgress,
      signal: syncAbort.signal,
    })
  }

  // Per-entity replica-shape migration. A bumped REPLICA_VERSION (projection
  // changed) or a brand-new entity (no stored version) means the local blobs are
  // the wrong shape, and forward sync can't fix it (the rows didn't change, so no
  // change rows fire). Wipe just that entity and re-backfill it from seq 0; the
  // global cursor stays at head so other entities are untouched. A fresh install
  // needs no backfill — its co-located cursor is also at 0, so forward sync fills.
  async function ensureReplicaShape(): Promise<void> {
    const executor = await getCatalogExecutor()
    const isFreshInstall = (await getCursor(executor)) === '0'
    let needsBackfill = false
    for (const [entity, version] of Object.entries(REPLICA_VERSION)) {
      const stored = await getEntityVersion(executor, entity)
      if (stored === version) continue
      // Changed projection: drop the stale-shaped rows. New entity (stored
      // undefined): nothing to wipe yet.
      if (stored !== undefined) await wipeEntity(executor, entity)
      // Fresh install (cursor 0): forward sync from 0 fills everything, no
      // backfill needed. Existing install: the entity's historical rows sit at
      // seq <= cursor, so forward sync alone never pulls them -> backfill.
      if (!isFreshInstall) needsBackfill = true
    }
    if (needsBackfill) await runBackfill(forwardSubscription.value)
    for (const [entity, version] of Object.entries(REPLICA_VERSION)) {
      await setEntityVersion(executor, entity, version)
    }
  }

  // Durable reconcile of the medium subscription against what's actually in the
  // replica. The subscription is pinned to every type now (the per-type UX was
  // dropped), so in practice this only widens installs that narrowed their
  // subscription while the toggles existed, and resumes a crash-interrupted
  // backfill. The widen/narrow machinery stays for when the dimension returns.
  async function reconcileSubscription(): Promise<void> {
    if (!canSync.value) return
    const executor = await getCatalogExecutor()
    const stored = await getSubscribedTypes(executor)
    const current: string[] = [...ALL_CATALOG_SUBSCRIPTION_TYPES]
    if (stored === undefined) {
      await setSubscribedTypes(executor, current)
      return
    }
    const added = current.filter((type) => !stored.includes(type))
    const removed = stored.filter((type) => !current.includes(type))
    if (added.length > 0) {
      // Prefer the prebuilt per-type blob (one download) over a from-0 feed
      // backfill; fall back to the feed if blobs are unavailable/incompatible.
      const region = catalogSubscription.value.regions[0] ?? 'dach'
      const outcome = await applyTypeBlobs({
        executor,
        types: added,
        region,
        report: reportProgress,
        signal: syncAbort.signal,
      })
      if (outcome === 'skipped') {
        await runBackfill({ types: added, regions: catalogSubscription.value.regions })
      }
    }
    if (added.length > 0 && packTier.value) {
      await downloadCoverArchives({
        types: added,
        tier: packTier.value,
        report: reportProgress,
      })
    }
    if (removed.length > 0) {
      await prune(executor, removed)
      await pruneCoverCache(executor)
    }
    if (added.length > 0 || removed.length > 0) {
      await setSubscribedTypes(executor, current)
    }
  }

  // Durable reconcile of the cover-pack tier against what's actually on the
  // Filesystem store, keyed by the persisted coverPackTier. A tier switch
  // re-downloads (the unzip overwrites the old tier's files in place); an
  // explicit 'none' clears the store. 'skipped' (manifest unreachable /
  // incompatible / aborted by a newer tier choice) leaves the stored tier
  // untouched so the next sync retries against the current preference.
  let coverPackAbort: AbortController | null = null
  async function reconcileCoverPack(): Promise<void> {
    if (!canSync.value) return
    const executor = await getCatalogExecutor()
    const stored = await getCoverPackTier(executor)
    const tier = packTier.value
    if (tier === null) {
      if (offlineImageTier.value === 'none' && stored !== undefined && stored !== 'none') {
        await clearCoverCache()
        await setCoverPackTier(executor, 'none')
      }
      return
    }
    if (stored === tier) return
    // Cover archives illustrate the catalog — pointless (and a wasted ~120 MB)
    // without the base snapshot. Never fetch them before the replica is installed:
    // a fresh install still at cursor 0 (bootstrap failed / not run yet) or a
    // surfaced-unavailable replica defers until a bootstrap succeeds, after which
    // performSync re-runs this reconcile.
    if (replicaUnavailable.value || (await getCursor(executor)) === '0') return
    // Cover archives are the second metered download — same WiFi gate as the
    // snapshot blob. Clearing/pruning above needs no network, so it stays ungated.
    if (!canDownloadBlobs.value) {
      awaitingWifi.value = true
      return
    }
    coverPackAbort = new AbortController()
    const outcome = await downloadCoverArchives({
      types: [...ALL_CATALOG_SUBSCRIPTION_TYPES],
      tier,
      signal: coverPackAbort.signal,
      report: reportProgress,
    })
    coverPackAbort = null
    if (outcome === 'applied') {
      await setCoverPackTier(executor, tier)
      awaitingWifi.value = false
    }
  }

  // Forward sync advances the single global cursor. resyncRequired (a retention
  // gap) wipes every replicated entity and restarts from seq 0.
  async function performSync(): Promise<void> {
    if (!canSync.value) return
    await serialize(() =>
      track(async () => {
        const executor = await getCatalogExecutor()
        const isFreshInstall = (await getCursor(executor)) === '0'
        if (isFreshInstall) {
          // The initial snapshot is the big metered download — hold it until WiFi
          // (or an explicit cellular override). The waiting flag drives the Settings
          // "waiting for WiFi" row + its force-download button; the wifi watcher
          // retriggers this sync once WiFi connects.
          if (!canDownloadBlobs.value) {
            console.warn(
              `Offline bootstrap parked: not on WiFi (connectionType not 'wifi', no cellular override)`,
            )
            awaitingWifi.value = true
            return
          }
          const outcome = await tryBootstrap(executor)
          if (outcome !== 'applied') {
            // Mandatory blob: never drain the whole feed from seq 0. Leave the
            // cursor at 0 and surface unavailability so a later retry (blob
            // deployed / reachable / version-matched) can seed the replica.
            console.warn(
              'Offline bootstrap skipped: replica unavailable (see manifest warning above)',
            )
            replicaUnavailable.value = true
            return
          }
          replicaUnavailable.value = false
          awaitingWifi.value = false
        }
        await ensureReplicaShape()
        await reconcileSubscription()
        let cursor = await getCursor(executor)
        let startSeq = cursor
        syncProgress.value = null
        syncPhase.value = 'updating'
        const deliveredCoverKeys = new Set<string>()
        // Cover strings each page strands (old value of a changed row, a tombstoned
        // row's cover), read pre-apply so a targeted prune can free just those files.
        const supersededCoverStrings: string[] = []
        let didResync = false
        for (;;) {
          // Stop between pages on logout teardown — the prior applyDelta transaction
          // has committed, so there is no open transaction to strand the write lock.
          if (syncAbort.signal.aborted) break
          const page = await pullPage(cursor, forwardSubscription.value)
          if (!page) break
          if (page.resyncRequired) {
            for (const entity of Object.keys(REPLICA_VERSION)) {
              await wipeEntity(executor, entity)
            }
            // The wipe invalidates what we believe is already materialized.
            writtenMembership.clear()
            await setCursor(executor, '0')
            cursor = '0'
            startSeq = '0'
            didResync = true
            continue
          }
          if (packTier.value) {
            for (const cover of await collectSupersededCoverStrings(executor, page)) {
              supersededCoverStrings.push(cover)
            }
          }
          await applyDelta(executor, page)
          for (const key of collectCoverKeys(page)) deliveredCoverKeys.add(key)
          await setCursor(executor, page.nextSeq)
          cursor = page.nextSeq
          updateProgress(startSeq, page)
          if (!page.hasMore) break
        }
        await reconcileCoverPack()
        if (packTier.value && deliveredCoverKeys.size > 0) {
          await fetchMissingCovers([...deliveredCoverKeys], packTier.value)
        }
        // A resync wipes the old rows targeted pruning would have read, so fall back to
        // the full scan there; the steady state prunes only the covers a sync stranded.
        if (didResync) {
          await pruneCoverCache(executor)
        } else if (packTier.value) {
          await pruneSupersededCovers(executor, supersededCoverStrings)
        }
        const now = dayjs().toISOString()
        await setLastSyncedAt(executor, now)
        lastSyncedAt.value = now
        // The per-transaction flush is throttled, so persist the final state of
        // this sync now instead of leaving it for pagehide.
        await flushCatalogStore()
      }),
    )
  }

  // Widening the subscription (added type/region) or a per-entity shape migration:
  // scoped from-zero drain that fills the slice. Deliberately does NOT touch the
  // global forward cursor (its historical rows sit at seq <= cursor); forward sync
  // keeps the slice current afterwards.
  // Raw (unserialized) backfill — only for callers already inside serialize()/
  // track() (ensureReplicaShape, reconcileSubscription). External callers use the
  // serialized `backfill` wrapper below so they can't open a second overlapping
  // SQLite transaction against performSync()/disableOfflineMode().
  async function runBackfill(subscription: CatalogSubscription): Promise<void> {
    if (!canSync.value) return
    const executor = await getCatalogExecutor()
    syncPhase.value = 'migrating'
    syncProgress.value = null
    let since = '0'
    for (;;) {
      if (syncAbort.signal.aborted) break
      const page = await pullPage(since, subscription)
      if (!page) break
      await applyDelta(executor, page)
      since = page.nextSeq
      updateProgress('0', page)
      if (!page.hasMore) break
    }
  }

  async function backfill(subscription: CatalogSubscription): Promise<void> {
    await serialize(() => track(() => runBackfill(subscription)))
  }

  // User opts in (consent modal / OfflineSync toggle): flip the pref, then run the
  // (initial, blocking-with-progress) bootstrap. The caller watches syncProgress /
  // syncing to render a progress UI and closes it when the promise resolves.
  async function enableOfflineMode(): Promise<void> {
    resetTeardown()
    await setOfflineModeEnabled(true)
    await performSync()
  }

  // Settings "download now over cellular": run the WiFi-gated download once with
  // the override on, then drop it so the gate is back in force for later syncs.
  async function forceCellularDownload(): Promise<void> {
    forceCellular.value = true
    try {
      await performSync()
    } finally {
      forceCellular.value = false
    }
  }

  // Logout teardown, awaited by auth.signOut BEFORE the webview reloads. Abort any
  // in-flight replica write (it stops at the next committed chunk/page boundary),
  // wait for the serial queue to fully drain so no transaction is open, then close
  // the native SQLite connection. This is what prevents the interrupted-transaction
  // write lock that otherwise strands the replica for the next session.
  async function prepareForLogout(): Promise<void> {
    tearingDown.value = true
    syncAbort.abort()
    coverPackAbort?.abort()
    try {
      await syncTail
    } catch {
      // Per-link errors are already swallowed; we only need the queue idle.
    }
    writtenMembership.clear()
    await closeCatalogStore()
  }

  // User opts out: stop syncing + reclaim the replica space.
  async function disableOfflineMode(): Promise<void> {
    await setOfflineModeEnabled(false)
    await serialize(async () => {
      const executor = await getCatalogExecutor()
      for (const entity of Object.keys(REPLICA_VERSION)) {
        await wipeEntity(executor, entity)
      }
      await setCursor(executor, '0')
    })
    lastSyncedAt.value = null
    replicaUnavailable.value = false
  }

  // Lifecycle entry point, called once from main like the other stores' init().
  // Creating the store already registered the offline read handlers + replica gate
  // synchronously (the for-loops + setReplicaGate above), so calling init() early
  // — before any store that issues a catalog read — guarantees reads route to the
  // replica when offline. This then kicks the initial fill and re-syncs on
  // reconnect. Not awaited: the fill is non-blocking and must not delay boot.
  // Force-offline pins the app to the replica; it can only be turned on once a full
  // sync has stamped lastSyncedAt. If the app boots force-offline but the replica
  // never synced (data lost / reset), pinning would trap the user with no data —
  // drop it and put them back online with a heads-up.
  async function recoverFromMissingReplica(): Promise<void> {
    if (!forceOffline.value || !canUseOffline.value) return
    const executor = await getCatalogExecutor()
    if (await getLastSyncedAt(executor)) return
    await setForceOffline(false)
    void showToast({ text: t('offlineDataVanished'), duration: 'long' })
  }

  function init(): void {
    void recoverFromMissingReplica()
    performSync().catch((error) => console.warn('Catalog sync failed', error))
    watch(connected, (isConnected) => {
      if (isConnected) performSync().catch((error) => console.warn('Catalog sync failed', error))
    })
    watch(appInForeground, (inForeground) => {
      if (!inForeground) {
        abortSyncAtNextCursorCheckpoint()
        return
      }
      if (tearingDown.value) return
      renewSyncAbort()
      performSync().catch((error) => console.warn('Catalog sync failed', error))
    })
    // A WiFi-gated download parked in awaitingWifi resumes the moment WiFi connects.
    watch(isWifi, (wifi) => {
      if (wifi && offlineEnabled.value && awaitingWifi.value) {
        performSync().catch((error) => console.warn('Catalog sync failed', error))
      }
    })
    // Re-login on a reused JS context (no webview reload): clear the logout
    // teardown so sync works again. Reset only — a fresh sync isn't kicked here
    // because a reload may still follow, and starting a write before it would
    // re-strand the DB.
    watch(
      () => authState.value.sessionExists,
      (exists, was) => {
        if (exists && !was) resetTeardown()
      },
    )
  }

  return {
    init,
    canUseOffline,
    offlineEnabled,
    canSync,
    performSync,
    enableOfflineMode,
    disableOfflineMode,
    forceCellularDownload,
    prepareForLogout,
    backfill,
    syncing,
    syncProgress,
    syncPhase,
    lastSyncedAt,
    replicaUnavailable,
    awaitingWifi,
    loadSyncMeta,
    getStorage,
    getImageStorage,
  }
})
